import { api } from './client';
import type { Order, OrderStatus, PaginatedResponse, Role } from '@/types/domain';

export interface OrderListParams {
  status?: OrderStatus;
  role?: Role;
  cursor?: string | null;
}

export async function fetchOrders(params: OrderListParams = {}): Promise<PaginatedResponse<Order>> {
  return api.get('/orders/', {
    query: {
      status: params.status,
      role: params.role,
      cursor: params.cursor ?? undefined,
    },
  });
}

export async function fetchOrder(id: number): Promise<Order> {
  return api.get(`/orders/${id}/`);
}

export interface CreateOrderInput {
  /** Legacy specialist-proposal flow uses an existing chat; client-side
   *  service-order flow (F-170 B4.4) omits `chat_id` so the backend can
   *  spawn a dedicated per-order chat (B0.12). */
  chat_id?: number;
  preset_id?: number;
  title?: string;
  description?: string;
  price_eur?: number;
  duration_minutes?: number;
  scheduled_at?: string;
  /** F-170 B4.4: free-text fields used by ServiceOrderForm. */
  address_text?: string;
  comment?: string;
}

export interface CreateOrderResponse extends Order {
  /** F-170 B0.12: when the backend created a dedicated chat thread for the
   *  order it surfaces the id here so the FE can navigate straight in. */
  chat_id: number;
}

export async function createOrder(input: CreateOrderInput): Promise<CreateOrderResponse> {
  return api.post('/orders/', { body: input });
}

export async function acceptOrder(id: number): Promise<Order> {
  return api.post(`/orders/${id}/accept/`);
}

export async function startOrder(id: number): Promise<Order> {
  return api.post(`/orders/${id}/start/`);
}

export async function completeOrder(id: number): Promise<Order> {
  return api.post(`/orders/${id}/complete/`);
}

export interface CancelInput {
  reason: string;
  confirm_outside_range?: boolean;
}

export interface CancelResponse {
  ok: boolean;
  rating_impact_warning?: string;
  specialist_can_sanction?: boolean;
}

export async function cancelOrder(id: number, input: CancelInput): Promise<CancelResponse> {
  return api.post(`/orders/${id}/cancel/`, { body: input });
}

export async function sanctionClient(
  orderId: number,
  kind: 'note' | 'block' | 'none',
  text?: string,
): Promise<void> {
  await api.post(`/orders/${orderId}/sanction/`, { body: { kind, text } });
}

export async function fetchUpcoming(): Promise<{ results: Order[] }> {
  return api.get('/orders/me/upcoming/');
}
